import { motion } from "framer-motion";
import { Rocket, Users, Lightbulb, GraduationCap, ExternalLink } from "lucide-react";
import { useLang } from "@/i18n";

const pillarIcons = [Rocket, GraduationCap, Lightbulb, Users];

const gallerySrcs = [
  "/assets/realizzazione-1.webp",
  "/assets/realizzazione-4.webp",
  "/assets/realizzazione-3.webp",
  "/assets/realizzazione-2.webp",
];

export default function Startup() {
  const { t } = useLang();
  const pillars = t.startup.pillars.map((p, i) => ({ ...p, icon: pillarIcons[i] }));
  const gallery = gallerySrcs.map((src, i) => ({ src, caption: t.startup.gallery[i] }));

  return (
    <section id="startup" className="py-24 sm:py-32 border-b border-white/10" data-testid="startup-section">
      <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-14 lg:gap-20 items-start">
        <div>
          <motion.p initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="overline-tag" data-testid="startup-overline">
            {t.startup.overline}
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}
            className="mt-4 font-display font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl"
          >
            {t.startup.h2}
          </motion.h2>
          <div className="mt-10 space-y-4">
            {pillars.map((p, i) => (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.12 }}
                className="glass rounded-2xl p-7 flex gap-5" data-testid={`pillar-card-${i}`}
              >
                <p.icon className="text-volt shrink-0 mt-1" size={24} strokeWidth={1.5} />
                <div>
                  <h3 className="font-display font-semibold text-lg">{p.title}</h3>
                  <p className="mt-2 text-sm text-steel leading-relaxed">{p.text}</p>
                  {p.link && (
                    <a href={p.link.href} target="_blank" rel="noopener noreferrer"
                      className="mt-3 inline-flex items-center gap-1.5 text-xs font-medium text-volt hover:underline" data-testid="patic-article-link">
                      {p.link.label} <ExternalLink size={12} />
                    </a>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
          className="relative rounded-2xl overflow-hidden border border-white/10 lg:sticky lg:top-28"
        >
          <img src="/assets/parabola.webp" alt="Diamond Sky — field installation" loading="lazy" className="w-full h-[420px] lg:h-[560px] object-cover grayscale-[35%] contrast-125" data-testid="startup-image" />
          <div className="absolute inset-0 bg-gradient-to-t from-anthracite/90 via-transparent to-transparent" />
          <div className="absolute bottom-6 left-6 right-6">
            <p className="text-xs tracking-[0.2em] uppercase text-volt">Diamond Sky</p>
            <p className="mt-2 text-sm text-white/80 max-w-sm">{t.startup.imgCaption}</p>
          </div>
        </motion.div>
      </div>

      <div className="max-w-7xl mx-auto px-6 mt-20">
        <motion.p initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="overline-tag">
          {t.startup.galleryOverline}
        </motion.p>
        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {gallery.map((photo, i) => (
            <motion.figure
              key={photo.src}
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="group relative rounded-xl overflow-hidden border border-white/10" data-testid={`gallery-photo-${i}`}
            >
              <img src={photo.src} alt={photo.caption} loading="lazy"
                className="w-full aspect-[4/3] object-cover grayscale-[40%] contrast-125 transition-[filter,transform] duration-500 group-hover:grayscale-0 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-anthracite/85 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <figcaption className="absolute bottom-3 left-4 right-4 text-xs text-white/85 translate-y-2 opacity-0 group-hover:opacity-100 group-hover:translate-y-0 transition-[opacity,transform] duration-500">
                {photo.caption}
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
}
